import { useEffect, useRef, useState } from "react";
import "./TextPromptModal.css";

interface TextPromptModalProps {
  open: boolean;
  title: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  submitLabel?: string;
  cancelLabel?: string;
  /** Optional: validate the input. Return a string error message to block submit. */
  validate?: (value: string) => string | null;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function TextPromptModal({
  open,
  title,
  description,
  placeholder,
  defaultValue = "",
  submitLabel = "OK",
  cancelLabel = "Cancel",
  validate,
  onConfirm,
  onCancel,
}: TextPromptModalProps) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError(null);
      // Focus the input once the dialog renders.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [open, defaultValue]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Please enter a value");
      return;
    }
    if (validate) {
      const validation = validate(trimmed);
      if (validation) { setError(validation); return; }
    }
    onConfirm(trimmed);
  };

  return (
    <div className="text-prompt-overlay" onClick={onCancel}>
      <div
        className="text-prompt-panel"
        role="dialog"
        aria-labelledby="text-prompt-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="text-prompt-title" className="text-prompt-title">{title}</h2>
        {description && <p className="text-prompt-description">{description}</p>}
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            className="text-prompt-input"
            value={value}
            placeholder={placeholder}
            onChange={(e) => { setValue(e.target.value); setError(null); }}
          />
          {error && <div className="text-prompt-error">{error}</div>}
          <div className="text-prompt-actions">
            <button type="button" className="text-prompt-btn" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button type="submit" className="text-prompt-btn text-prompt-btn--primary">
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
